import React, {useCallback, useEffect} from 'react';
import {useTranslation} from "react-i18next";

import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';

import {BaseDataType} from "../types";
import {IMAGE_FILETYPES} from "../../../Utils/const";
import ImageZoom from "./ImageZoom";
import IconsDescription from "./IconsDescription";
import FullscreenDialog from "./FullscreenDialog";

type ReactPanZoomProps = {
    alt?: string;
    ref?: any;
    src: string;
    width?: string;
    height?: string;
    type?: "id" | "signature",
    styles?: { [key: string]: string | number }
    baseData?: BaseDataType;
    recruitEmail?: string;
    mandate: number;
};

const ZoomPanRotateImage: React.FC<ReactPanZoomProps> = (props) => {
    const {t} = useTranslation('common');

    const {
        alt, ref, src, width = (props.type === "signature") ? "300px" : "500px", height = "200px", type = "id",
        styles,
        baseData,
        recruitEmail,
        mandate,
    } = props;

    const [dx, setDx] = React.useState(0);
    const [dy, setDy] = React.useState(0);
    const [zoom, setZoom] = React.useState(1);
    const [rotation, setRotation] = React.useState(0);
    const [rotationInDrawer, setRotationInDrawer] = React.useState(0);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<boolean>(false);
    const [fileData, setFileData] = React.useState<string>("");
    const [fileType, setFileType] = React.useState<string>("");
    const [openFullScreen, setOpenFullScreen] = React.useState<boolean>(false);

    const resetAll = () => {
        setDx(0);
        setDy(0);
        setZoom(1);
        setRotation(0);
        // setFlip(false);
    };

    const zoomIn = () => {
        setZoom(zoom + 0.2);
    };

    const zoomOut = () => {
        if (zoom >= 1) {
            setZoom(zoom - 0.2);
        }
    };

    const rotateLeft = () => {
        if (rotation === -3) {
            setRotation(0);
        } else {
            setRotation(rotation - 1);
        }
    };

    const onPan = (dx: number, dy: number) => {
        setDx(dx);
        setDy(dy);
    };

    const loadFileData = useCallback(() => {
        fetch(src).then(response => {
            if (!response.ok) {
                getFetchError();
                return;
            }

            response.text().then((text) => {
                if (text === "") {
                    getFetchError();
                    return
                }
                setFileType(text.split(':')[1].split(';')[0]);
                setFileData(text);
                setLoading(false);
            }).catch(_ => getFetchError())
        }).catch((_) => getFetchError());
    }, [src])

    useEffect(() => {
        loadFileData();
    }, [loadFileData]);

    const getFetchError = () => {
        setError(true)
        setLoading(false);
    }

    const closeFullscreen = () => {
        setOpenFullScreen(false);
    }

    if (loading) {
        return <div>
            <LinearProgress/>
            <Skeleton variant="rectangular" width={width} height={height}/>
        </div>
    }

    if (error) {
        return <div>
            <Alert severity="error">{t("error_occurred")}</Alert>
        </div>
    }

    return (
        <Box sx={{width: width}}>
            {type === "id" ? <>
                <Box sx={{display: "flex", justifyContent: "center"}}>
                    <IconButton onClick={zoomIn}>
                        <AddIcon/>
                    </IconButton>
                    <IconButton onClick={zoomOut}>
                        <RemoveIcon/>
                    </IconButton>
                    <IconButton onClick={rotateLeft}>
                        <Rotate90DegreesCcwIcon/>
                    </IconButton>
                    <IconButton onClick={resetAll}>
                        <UnfoldLessIcon/>
                    </IconButton>
                    <IconButton onClick={() => setOpenFullScreen && setOpenFullScreen(true)}>
                        <FullscreenIcon/>
                    </IconButton>
                    <Tooltip title={<IconsDescription/>} placement="top" arrow>
                        <IconButton>
                            <QuestionMarkIcon color="inherit"/>
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box style={{
                    width: width,
                    position: "relative",
                    overflow: "hidden",
                }}>

                    <ImageZoom
                        style={{
                            width: width,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        zoom={zoom}
                        setZoom={setZoom}
                        pandx={dx}
                        pandy={dy}
                        onPan={onPan}
                        // rotation={rotation}
                        key={dx}
                    >
                        {(IMAGE_FILETYPES.includes(fileType)) ?
                            <img
                                style={{
                                    transform: `rotate(${rotation * 90}deg)`,
                                    width: width,
                                }}
                                src={fileData}
                                alt={alt}
                                ref={ref}
                            /> :
                            <iframe src={fileData}
                                    style={{width: width, transform: `rotate(${rotation * 90}deg)`, minHeight: "100px"}}
                                    title={new Date().getTime() + ""}/>
                        }
                    </ImageZoom>
                </Box>
            </> : <img src={fileData} alt="signature" style={{width, ...styles}}/>}
            {type === "id" && baseData && recruitEmail ?
                <FullscreenDialog mandate={mandate} rotationInDrawer={rotationInDrawer} setRotationInDrawer={setRotationInDrawer} fileData={fileData} fileType={fileType} baseData={baseData} recruitEmail={recruitEmail} closeFullscreen={closeFullscreen} openFullScreen={openFullScreen}/>
                : null}
        </Box>
    );
};

export default ZoomPanRotateImage;